<script setup>
// import usei18n from '@/core/util/composable/usei18n';
import {
  computed,
  reactive,
  ref,
  defineProps,
  inject,
  onMounted,
  watch,
} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@/core/util/composable/usePaginate';
import DuplicateDialog from '@ohrm/components/dialogs/DuplicateDialog';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {navigate} from '@/core/util/helper/navigation';
import States from '../constants/States';
import TrainigTypes from '../constants/TrainingTypes';
import UserRoles from '../constants/UserRoles';
import SwitchInput from '@ohrm/oxd/core/components/Input/SwitchInput';
import Spinner from '@ohrm/oxd/core/components/Loader/Spinner';
import Tooltip from '@/core/components/inputs/IconButton';

const props = defineProps({
  showPaginator: {type: Boolean, default: true},
});

const defaultFilters = {
  owners: [],
  trainers: [],
  assignes: [],
  startDate: null,
  dueDate: null,
  course: '',
  name: '',
  departments: '',
  subUnits: [],
  types: '',
  status: [],
};

const isEmployee = computed(() => {
  return [UserRoles.ESS.id].includes(window.appGlobal.user.roleId);
});

const isAdmin = computed(() => {
  return [UserRoles.ADMIN.id].includes(window.appGlobal.user.roleId);
});

const isSupervisor = computed(() => {
  return [UserRoles.SUPERVISOR.id].includes(window.appGlobal.user.roleId);
});

let filters = reactive({...defaultFilters});

const normalizeFilters = ref({...defaultFilters});

const checkedItems = ref([]);
const subUnits = ref([]);

const http = new APIService(window.appGlobal.baseUrl, `api/training`);

const informationNormalizer = data => {
  return data.map(item => {
    return {
      id: item.id,
      encryptedTrainingId: item.encrypted_training_id,
      internalId: `TRN-${item.id < 10 ? '0' : ''}${item.id}`,
      training: item.title,
      owner: item.owner,
      ownerId: item.owner_id,
      trainer: item.trainer.map(val => val.name).join(' / '),
      department: item.department.map(val => val.name).join(' / '),
      subUnit: item.subunit.map(val => val.name).join(' / '),
      startDate: item.start_date,
      dueDate: item.due_date,
      type: TrainigTypes.find(val => val.id == item.training_type).label,
      typeId: item.training_type,
      status: States.find(val => val.id == item.status).label,
      statusId: item.status,
      certifications: item.certifications,
      encryptedEmployeeId: item.encrypted_employee_id,
      relevantJob: item.relevant_job,
      _loading: item._loading,
    };
  });
};

const {
  showPaginator,
  currentPage,
  total,
  pages,
  pageSize,
  response,
  isLoading,
  execQuery,
} = usePaginate(http, {
  normalizer: informationNormalizer,
  query: normalizeFilters,
  pageSize: 15,
  toastNoRecords: false,
});

const duplicateDialog = ref(null);
const deleteDialog = ref(null);

const deleteItems = items => {
  if (items instanceof Array) {
    http
      .deleteAll({
        ids: items,
      })
      .then(() => {
        // return $toast.deleteSuccess();
      })
      .finally(() => {
        execQuery();
      });
  }
};

const onClickDelete = item => {
  deleteDialog.value.showDialog().then(confirmation => {
    if (confirmation === 'ok') {
      deleteItems([item.id]);
    }
  });
};

const onClickEdit = item => {
  navigate('/training/training-plan/{id}/edit', {id: item.id});
};

const onClickView = item => {
  navigate('/training/viewTrainingPlan/{id}', {id: item.id});
};

const duplicate = async item => {
  await http
    .request({
      method: 'POST',
      url: `api/training/${item.id}/duplicate`,
    })
    .then(() => {
      // console.log(response);
      //onClickEdit({id: response.data});
    });
};

const onClickDuplicate = item => {
  duplicateDialog.value.showDialog().then(confirmation => {
    if (confirmation === 'ok') {
      duplicate(item);
    }
  });
};

const structuredClone = object => {
  return JSON.parse(JSON.stringify(object));
};

const onReset = () => {
  filters = {...defaultFilters};
  normalizeFilters.value = structuredClone(filters);

  execQuery();
};

const onSubmit = () => {
  normalizeFilters.value = structuredClone(filters);
  normalizeFilters.value.status = normalizeFilters.value.status.map(
    item => item.id,
  );
  normalizeFilters.value.types = normalizeFilters.value.types.id;
  normalizeFilters.value.owners = normalizeFilters.value.owners.map(
    item => item.id,
  );
  normalizeFilters.value.trainers = normalizeFilters.value.trainers.map(
    item => item.label,
  );

  if (normalizeFilters.value.departments?.id)
    normalizeFilters.value.departments = [
      normalizeFilters.value.departments.id,
    ];
  normalizeFilters.value.subUnits = normalizeFilters.value.subUnits.map(
    item => item.id,
  );

  execQuery();
};

const goToCreate = () => {
  window.location = `${window.appGlobal.baseUrl}/training/training-plan`;
};

const openOrGeneratePdf = item => {
  const isExternal = TrainigTypes[1].id == item.typeId;
  if (isExternal)
    window.open(
      `${window.appGlobal.publicPath}/${item.certifications[0]}`,
      '_blank',
    );
  else {
    window.open(
      `${window.appGlobal.publicPath}/api/training/${item.encryptedTrainingId}/pdf/${item.encryptedEmployeeId}`,
    );
  }
};

const renderRelevanteJob = (...args) => {
  const [index, , , row] = args;

  const loader = {
    component: Spinner,
    props: {
      withContainer: false,
    },
  };

  const switchSubscription = {
    component: SwitchInput,
    props: {
      modelValue: row.relevantJob,
      'onUpdate:modelValue': async $event => {
        response.value.data[index]._loading = true;

        await http.request({
          method: 'POST',
          url: `api/training-participants/${row.id}/${window.appGlobal.user.id}`,
          data: {
            relevantJob: !row.relevantJob,
          },
        });

        response.value.data[index].relevantJob = !row.relevantJob;
        response.value.data[index]._loading = false;
      },
      style: {
        'margin-left': '1rem',
        'text-align': 'left',
      },
    },
  };

  return {
    props: {
      header: {
        cellConfig: {
          ...(row._loading ? {loader} : {switchSubscription}),
        },
      },
    },
  };
};

const generateActions = (...args) => {
  const [index, , , row] = args;

  let actions = {};

  if (
    row.ownerId == window.appGlobal.user.id ||
    window.appGlobal.user.roleId == 1
  ) {
    actions = {
      edit: {
        onClick: onClickEdit,
        component: Tooltip,
        props: {
          name: 'pencil-fill',
          title: 'Edit',
        },
      },
      view: {
        onClick: onClickView,
        component: Tooltip,
        props: {
          name: 'eye',
          title: 'Detail',
        },
      },
      duplicate: {
        onClick: onClickDuplicate,
        component: Tooltip,
        props: {
          name: 'files',
          title: 'Duplicate',
        },
      },
      delete: {
        onClick: onClickDelete,
        component: Tooltip,
        props: {
          name: 'trash',
          title: 'Delete',
        },
      },
    };
  } else {
    actions = {
      view: {
        onClick: onClickView,
        component: Tooltip,
        props: {
          name: 'eye',
          title: 'Detail',
        },
      },
    };

    if (row.statusId === 3) {
      let isAllow = true;

      const isExternal = TrainigTypes[1].id == row.typeId;
      if (isExternal && row.certifications == null) {
        isAllow = false;
      }

      if (isAllow) {
        actions.download = {
          onClick: openOrGeneratePdf,
          component: Tooltip,
          props: {
            name: 'download',
            title: 'Certification',
          },
        };
      }
    }
  }

  return {
    props: {
      header: {
        cellConfig: actions,
      },
    },
  };
};

const headers = ref([
  {name: 'internalId', title: 'Id', style: {flex: 1}},
  {name: 'training', title: 'Training', style: {flex: 1}},
  {name: 'owner', title: 'Owner', style: {flex: 1}},
  {name: 'trainer', title: 'Trainer', style: {flex: 1}},
  {name: 'department', title: 'Department', style: {flex: 1}},
  {name: 'subUnit', title: 'Sub Unit', style: {flex: 1}},
  {name: 'startDate', title: 'Start Date', style: {flex: 1}},
  {name: 'dueDate', title: 'Due Date', style: {flex: 1}},
  {name: 'type', title: 'Type', style: {flex: 1}},
  {name: 'status', title: 'Status', style: {flex: 1}},
  {
    name: 'relevant_job',
    title: 'Relevant Job',
    style: {flex: 1},
    cellType: 'oxd-table-cell-actions',
    cellRenderer: renderRelevanteJob,
  },
  {
    name: 'actions',
    title: 'Actions',
    style: {flex: 1},
    cellType: 'oxd-table-cell-actions',
    cellRenderer: generateActions,
  },
]);

onMounted(() => {
  if (
    [UserRoles.ADMIN.id, UserRoles.SUPERVISOR.id].includes(
      window.appGlobal.user.roleId,
    )
  ) {
    headers.value.splice(10, 1);
  }
});

const getDepartmentSubUnits = departments => {
  if (departments) {
    const departmentIds = [departments.id];
    http
      .request({
        method: 'GET',
        url: `api/training/departments/sub-units`,
        params: {departmentIds},
      })
      .then(response => {
        subUnits.value = response.data;
      });
  } else {
    subUnits.value = [];
  }
};

// Watchts
watch(
  () => filters.departments,
  departments => {
    filters.subUnits = [];
    getDepartmentSubUnits(departments);
  },
);
</script>
<template>
  <div class="orangehrm-background-container">
    <slot
      :filters="filters"
      :onSubmit="onSubmit"
      :onReset="onReset"
      :subUnits="subUnits"
    ></slot>
    <br />
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <div v-if="checkedItems > 0" class="actions">
          <oxd-text tag="span">
            {{ $t('general.n_records_selected', {count: checkedItems.length}) }}
          </oxd-text>
        </div>
        <oxd-text v-else tag="span">
          {{ $t('general.n_records_found', {count: total}) }}
        </oxd-text>
      </div>
      <div
        v-if="!isEmployee"
        class="orangehrm-horizontal-padding orangehrm-vertical-padding"
      >
        <oxd-button
          :disabled="isEmployee"
          label="Add"
          size="medium"
          icon-name="plus"
          type="button"
          display-type="secondary"
          @click="goToCreate"
        />
      </div>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          :headers="headers"
          :items="response?.data"
          :selectable="false"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
    <DuplicateDialog
      ref="duplicateDialog"
      message="The selected record will be duplicated. Are you sure you want to continue?"
    ></DuplicateDialog>
    <DeleteConfirmationDialog ref="deleteDialog"></DeleteConfirmationDialog>
  </div>
</template>
<style lang="scss" scoped>
.orangehrm-header-container {
  .actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
  }

  span {
    margin-right: 20px;
  }
}
</style>
